import React, { useContext, useEffect } from "react";

import { useTranslation } from "react-i18next";

import useFAST from "../../../contexts/FASTContext";
import {
  WebSocketConnectionStatus,
  WebSocketContext,
} from "../../../contexts/WebSocket";
import useFASTScheduleWebSocket from "../../../hooks/useFASTScheduleWebSocket";
import Switch from "../../form/Switch";
import WebSocketStatus from "../../webSocket/WebSocketStatus";

import type { ChangeEvent, JSX } from "react";
import type { UseFormReturn } from "react-hook-form";

interface EditModeSwitchProps {
  methods: UseFormReturn<{
    isEditMode: boolean;
  }>;
  isDisabled: boolean;
}

export default function EditModeSwitch({
  methods,
  isDisabled,
}: EditModeSwitchProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const isEditMode = methods.watch("isEditMode");
  const { state } = useFAST();
  const { status } = useContext(WebSocketContext);

  const { isOwned, lockingAccount, isReleased } = state.scheduleEditInfo;

  const { lockSchedule, releaseSchedule } = useFASTScheduleWebSocket();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const isChecked = event.target.checked;
    if (isChecked) {
      lockSchedule();
    } else {
      releaseSchedule();
    }
  };

  const isExternalLock = Boolean(lockingAccount && !isEditMode);

  const isWebSocketOpen = status === WebSocketConnectionStatus.Open;

  useEffect(() => {
    if (!isWebSocketOpen || isReleased) {
      methods.setValue("isEditMode", false);
    }
  }, [methods, isWebSocketOpen, isReleased]);

  return (
    <div className="flex h-full gap-[0.78125rem]">
      {isExternalLock ? (
        <div className="border-destructive flex items-center gap-[0.78125rem] rounded-[0.46875rem] border-2 px-[0.9375rem] py-[0.625rem] font-medium">
          <div
            className="size-[1.875rem] shrink-0 rounded-full bg-cover bg-center"
            style={{
              backgroundImage: `url(${lockingAccount?.avatar})`,
            }}
          />
          {isOwned ? (
            <div>{t("youAreCurrentlyEditing")}</div>
          ) : (
            <div>
              {`${lockingAccount?.firstName} ${lockingAccount?.lastName} `}
              <span className="text-destructive">
                {t("isCurrentlyEditingThisSchedule")}
              </span>
            </div>
          )}
        </div>
      ) : null}
      <div className="border-border bg-card flex items-center justify-center gap-[0.78125rem] rounded-[0.46875rem] border px-[0.46875rem] py-[0.3125rem]">
        <WebSocketStatus />
        <Switch
          isCompact
          isNoWrap
          className="mb-0!"
          color="#d80027"
          control={methods.control}
          isReadOnly={isDisabled || isExternalLock || !isWebSocketOpen}
          label={t("editMode")}
          name="isEditMode"
          onChangeCustom={handleChange}
          size="sm"
        />
      </div>
    </div>
  );
}
